import { useState } from "react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { MessageSquareHeart, Reply } from "lucide-react";
import ReadAloud from "@/components/ReadAloud";

export default function FamilyNote({ note, compact = false, canReply = false, requestId, onReplied }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  if (!note?.text) return null;
  const speech = `A note from ${note.from}: ${note.text}${note.reply ? `. ${note.reply.from} replied: ${note.reply.text}` : ""}`;

  const send = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/requests/${requestId}/family-note-reply`, { text });
      toast.success(`Reply sent to ${note.from}`); setText(""); onReplied?.(data.reply);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="family-note" className={`bg-[#FFF8E6] border border-[#F3D98B] rounded-2xl ${compact ? "p-3 mt-3" : "p-5 mt-5"}`}>
      <div className="flex items-start gap-3">
        <MessageSquareHeart className="w-6 h-6 text-[#A9741A] shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-[#A9741A] tracking-wide">NOTE FROM {note.from.toUpperCase()}</p>
          <p className={`${compact ? "text-base" : "text-lg"} text-slate-800 mt-1`}>{note.text}</p>
          {note.reply && (
            <div data-testid="family-note-reply" className="mt-3 pl-3 border-l-4 border-[#2E7D6B]">
              <p className="text-sm font-semibold text-[#2E7D6B] tracking-wide">{note.reply.from.toUpperCase()} REPLIED</p>
              <p className={`${compact ? "text-base" : "text-lg"} text-slate-800`}>{note.reply.text}</p>
            </div>
          )}
        </div>
        <ReadAloud text={speech} testId="family-note-read" />
      </div>
      {canReply && (
        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <input data-testid="family-note-reply-input" value={text} onChange={(e) => setText(e.target.value)} maxLength={300}
            placeholder={note.reply ? "Send another reply…" : `Reply to ${note.from}, e.g. "Noted — I'll take it slow and call when we're back."`}
            className="flex-1 px-4 min-h-[52px] rounded-xl border border-[#E2D9CE] focus:border-[#2E7D6B] outline-none bg-white" />
          <button type="button" data-testid="family-note-reply-send" disabled={saving || !text.trim()} onClick={send}
            className="bg-[#2E7D6B] disabled:opacity-50 text-white font-semibold px-5 min-h-[52px] rounded-xl flex items-center justify-center gap-2"><Reply className="w-5 h-5" /> {saving ? "Sending…" : "Send reply"}</button>
        </div>
      )}
    </div>
  );
}
